import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import utilCardStyles from "../../../../common/components/xepp/utility-card/UtilityCard.module.scss";
import UtilityCard from "../../../../common/components/xepp/utility-card/UtilityCard";
import UtilityForm from "./UtilityForm";
import { useLazyGetUtilitiesByCountryIdQuery, useUpdateUtilityMutation } from "../../../../service/utility-service";
import CommonLoadingSpinner from "../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import { UtilityIcon } from "../../../../assets/icons";
import { Utility } from "../../../../model/bill-settings/bill-settings";
import { getErrorMessage } from "../../../../utils/error-handler";

interface UtilityPageProps {
	selectedCountryId: number | null;
	countryLoading: boolean;
}

export const UtilityPage = ({ selectedCountryId, countryLoading }: UtilityPageProps) => {
	const [selectedUtility, setSelectedUtility] = useState<Utility>();
	const [getUtilities, { data, isLoading }] = useLazyGetUtilitiesByCountryIdQuery();
	const [updateUtility, { isLoading: isUpdating }] = useUpdateUtilityMutation();

	useEffect(() => {
		if (selectedCountryId) {
			getUtilities(selectedCountryId);
		}
	}, [selectedCountryId]);

	const handleManageUtility = (utility: Utility) => {
		setSelectedUtility(utility);
	};

	const handleSaveUtility = async (formData: any) => {
		const submitFormData = new FormData();

		submitFormData.append("id", formData.id);
		submitFormData.append("name", formData.name);
		submitFormData.append("countryId", formData.countryId.toString());
		submitFormData.append("utilityCode", formData.utilityCode);
		submitFormData.append("tariff", formData.tariff);

		if (formData.chargeType === "percentage" && formData.percentageCharge) {
			submitFormData.append("percentageCharge", formData.percentageCharge);
		} else if (formData.chargeType === "fixed" && formData.fixedCharge) {
			submitFormData.append("fixedCharge", formData.fixedCharge);
		}

		if (formData.imageFile) {
			submitFormData.append("imageFile", formData.imageFile);
		}

		await updateUtility(submitFormData)
			.unwrap()
			.then(res => {
				setSelectedUtility(undefined);
				toast.success("Utility updated successfully");
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const UtilityTabContent = () => (
		<div className={utilCardStyles.utilityGrid}>
			{data &&
				data.length > 0 &&
				data.map(utility => (
					<div key={utility.utilityCode} className={utilCardStyles.utilityCardWrapper}>
						<UtilityCard logo={utility.imageUrl} name={utility.name} onManage={() => handleManageUtility(utility)} />
					</div>
				))}
		</div>
	);

	return (
		<div>
			<CommonLoadingSpinner loading={isLoading || countryLoading}>
				{data?.length === 0 && <EmptyState icon={<UtilityIcon className="mb-8" />} description="No utilities for the selected country" />}
				{selectedUtility ? (
					<UtilityForm isSaving={isUpdating} utility={selectedUtility} onCancel={() => {setSelectedUtility(undefined)}} onSave={handleSaveUtility} />
				) : (
					<UtilityTabContent />
				)}
			</CommonLoadingSpinner>
		</div>
	);
};
